/**
 * Legal — the reconciler, the one writer of `legalContentState`
 * (@momo/views). Mounted for the document's lifetime, so every reader
 * (checkout, /legal, sign-consent) reflects a publish without a reload. The
 * content-addressed pointer rides `workspaceCard` (@momo/views) — a read
 * mountWorkspaceCard (workspace.sync.ts) keeps live — so this file derives the pointer
 * from it and resolves each locale's blob by URL (legal.api.ts's
 * `legalContent$`), at zero additional Firestore reads.
 *
 * `distinctUntilChanged` gates on the pointer alone (not every workspace
 * card field), so an unrelated card edit (brand, wallet, …) never
 * re-triggers a blob refetch — content-addressing means a refetch of the
 * same sha would be a same-answer no-op anyway; this just skips the round
 * trip.
 *
 * Keyed on the active organization: a switch returns the cell to unsettled
 * defaults before the new organization's blob resolves, and so does a
 * `legalRetry$` request after a failure, which resolves the same organization
 * again. With none open — the bare host — a founder contracts with the
 * deployment's operator, so the cell carries `platform/legal`'s identity and
 * the operator's own texts, or names the record absent so consent refuses.
 *
 * mountPlatformLegal writes the second cell, `platformLegalState`: the
 * operator's own texts while an organization is open, read only while the
 * host says a founding invitation is on screen, with the same bound.
 *
 * Checkout pauses payment on `failed`, so a merely slow read stays unsettled
 * well past an ordinary wait; only after a long bound does it count as
 * failed, and a later answer still lands over it. `connect()` keeps the bound
 * and the answer on one underlying resolve.
 */

import {
	emptyLegalContent,
	type LegalContentState,
	legalContentState,
	legalRetry$,
	platformLegalState,
} from "@momo/views/legal";
import { organizationId } from "@momo/views/organization";
import { workspaceCard } from "@momo/views/workspace";
import {
	catchError,
	connect,
	distinctUntilChanged,
	filter,
	map,
	merge,
	type Observable,
	of,
	type Subscription,
	startWith,
	switchMap,
	takeUntil,
	tap,
	timer,
} from "rxjs";
import type { LegalReads } from "./legal.api.ts";

const SETTLE_TIMEOUT_MS = 15_000;

type PlatformReads = Pick<
	LegalReads,
	"platformLegal$" | "platformLegalContent$"
>;

/** The operator's record and its texts, or the record named absent so
 *  consent refuses: there is nobody to contract with. */
function operatorLegal$(
	reads: PlatformReads,
): Observable<LegalContentState | "failed"> {
	return reads.platformLegal$().pipe(
		// JSON.stringify walks the whole record, so an edit to any of its
		// fields, a field added later included, is caught.
		distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
		switchMap((record) =>
			record === null
				? of<LegalContentState>({
						settled: true,
						content: emptyLegalContent,
						operator: "absent",
					})
				: reads
						.platformLegalContent$(record)
						.pipe(
							map((content) =>
								content === "failed"
									? content
									: { settled: true, content, operator: record },
							),
						),
		),
	);
}

/** An answer bounded in time: a record that cannot be read, or that has not
 *  answered past the bound, is `failed` — never the default standing in for
 *  the text owed — and a later answer still lands over it. */
function bounded$(
	answer$: Observable<LegalContentState | "failed">,
	scope: string | null,
): Observable<LegalContentState | "failed"> {
	return answer$.pipe(
		catchError((err: unknown) => {
			console.warn("[legal] legal record unreadable", scope, err);
			return of("failed" as const);
		}),
		// Both legs subscribe before the read starts, so an answer that
		// arrives in the same tick still stops the bound. The bound restarts
		// with each scope and each retry.
		connect((answered$) =>
			merge(
				answered$,
				timer(SETTLE_TIMEOUT_MS).pipe(
					takeUntil(answered$),
					map(() => "failed" as const),
				),
			),
		),
	);
}

const unsettled: LegalContentState = {
	settled: false,
	content: emptyLegalContent,
};
const asState = (answer: LegalContentState | "failed"): LegalContentState =>
	answer === "failed"
		? { settled: true, content: emptyLegalContent, failed: true }
		: answer;

// This stays the one writer of the cell: the bare host's consent reads the
// same cell an organization's does, rather than a second cell or a second
// writer that could disagree with it.
export function mountLegal(
	reads: Pick<
		LegalReads,
		"legalContent$" | "platformLegal$" | "platformLegalContent$"
	>,
): Subscription {
	return organizationId.$.pipe(
		distinctUntilChanged(),
		switchMap((orgId) =>
			legalRetry$.pipe(
				startWith(undefined),
				map(() => orgId),
			),
		),
		tap(() => legalContentState.replace(unsettled)),
		switchMap((orgId) =>
			bounded$(
				orgId === null
					? operatorLegal$(reads)
					: // The open organization's card is already read live by the
						// workspace reconciler, tagged with the organization it was
						// read under.
						workspaceCard.$.pipe(
							filter(
								(card) => card.status !== "loading" && card.orgId === orgId,
							),
							map((card) => card.workspace?.legalContent ?? null),
							distinctUntilChanged(
								(a, b) => JSON.stringify(a) === JSON.stringify(b),
							),
							switchMap((pointer) => reads.legalContent$(orgId, pointer)),
							map((content) =>
								content === "failed" ? content : { settled: true, content },
							),
						),
				orgId,
			),
		),
		tap((answer) => legalContentState.replace(asState(answer))),
	).subscribe();
}

/**
 * The one writer of `platformLegalState`: the operator's own record and
 * texts, read only while `wanted$` says a founding invitation is on screen —
 * on an organization, where `legalContentState` carries the organization's
 * texts — and unsettled otherwise. A `legalRetry$` request reads them again.
 */
export function mountPlatformLegal(
	reads: PlatformReads,
	wanted$: Observable<boolean>,
): Subscription {
	return wanted$
		.pipe(
			distinctUntilChanged(),
			switchMap((wanted) =>
				wanted
					? legalRetry$.pipe(
							startWith(undefined),
							tap(() => platformLegalState.replace(unsettled)),
							switchMap(() => bounded$(operatorLegal$(reads), "platform")),
							map(asState),
						)
					: of(unsettled),
			),
			tap((answer) => platformLegalState.replace(answer)),
		)
		.subscribe();
}
